import { useCallback, useMemo } from 'react';
import { State } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/platform-enums/location_feature_enum.pb';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { FeatureFlagStrategy, useAppFlagStore } from '@frontend/shared';
import { useAppScopeStore } from './store';

interface GetCustomizationFlagValueFn {
  (flagName: Feature, state?: keyof typeof State, strategy?: FeatureFlagStrategy): ReturnType<
    ReturnType<typeof useAppFlagStore>['getCustomizationFlagValue']
  >;
  /**
   * @deprecated Use `getCustomizationFlagValue` with {@link Feature} enum instead of string.
   */
  (flagName: string, state?: keyof typeof State, strategy?: FeatureFlagStrategy): ReturnType<
    ReturnType<typeof useAppFlagStore>['getCustomizationFlagValue']
  >;
}

interface GetCustomizationFlagFn {
  (flagName: Feature): ReturnType<ReturnType<typeof useAppFlagStore>['getCustomizationFlag']>;
  /**
   * @deprecated Use `getCustomizationFlag` with {@link Feature} enum instead of string.
   */
  (flagName: string): ReturnType<ReturnType<typeof useAppFlagStore>['getCustomizationFlag']>;
}

interface GetCustomizationFlagLocationMapFn {
  (flagName: Feature): ReturnType<ReturnType<typeof useAppFlagStore>['getCustomizationFlagLocationMap']>;
  /**
   * @deprecated Use `getCustomizationFlagLocationMap` with {@link Feature} enum instead of string.
   */
  (flagName: string): ReturnType<ReturnType<typeof useAppFlagStore>['getCustomizationFlagLocationMap']>;
}

export const useScopedAppFlagStore = () => {
  const { selectedLocationIds } = useAppScopeStore();
  const {
    getFeatureFlagValue,
    getCustomizationFlagValue,
    getCustomizationFlag,
    getFeatureFlag,
    customizationFlagMap,
    customizationFlagIdEnumMap,
    getCustomizationFlagLocationMap,
    getFeatureFlagLocationMap,
    featureFlags,
  } = useAppFlagStore();

  const getScopedFeatureFlagValue = useCallback(
    (flagName: string, strategy: FeatureFlagStrategy = 'ANY') =>
      getFeatureFlagValue({ locationIds: selectedLocationIds, flagName, strategy }),
    [selectedLocationIds, getFeatureFlagValue]
  );

  const getScopedCustomizationFlagValue = useCallback<GetCustomizationFlagValueFn>(
    (flagName, state = 'ACTIVE', strategy = 'ANY') =>
      getCustomizationFlagValue({ locationIds: selectedLocationIds, flagName, strategy, state }),
    [selectedLocationIds, getCustomizationFlagValue]
  );

  const getScopedCustomizationFlag = useCallback<GetCustomizationFlagFn>(
    (flagName) => getCustomizationFlag({ locationIds: selectedLocationIds, flagName }),
    [selectedLocationIds, getCustomizationFlag]
  );

  const getScopedFeatureFlag = useCallback(
    (flagName: string) => getFeatureFlag({ locationIds: selectedLocationIds, flagName }),
    [selectedLocationIds, getFeatureFlag]
  );

  const getScopedCustomizationFlagLocationMap = useCallback<GetCustomizationFlagLocationMapFn>(
    (flagName) => getCustomizationFlagLocationMap({ locationIds: selectedLocationIds, flagName }),
    [selectedLocationIds, getCustomizationFlagLocationMap]
  );

  const getScopedFeatureFlagLocationMap = useCallback(
    (flagName: string) => getFeatureFlagLocationMap({ locationIds: selectedLocationIds, flagName }),
    [selectedLocationIds, getFeatureFlagLocationMap]
  );

  const getScopedCustomizationFlagValueById = useCallback(
    (flagId: string, state: keyof typeof State = 'ACTIVE', strategy: FeatureFlagStrategy = 'ANY') => {
      // TODO: Milind Remove customizationFlagMap fallback once all consumers use featureEnum
      const featureEnum = customizationFlagIdEnumMap[flagId];
      const flagName = customizationFlagMap[flagId];
      return flagName || featureEnum
        ? getCustomizationFlagValue({
            locationIds: selectedLocationIds,
            flagName,
            featureEnum,
            strategy,
            state,
          })
        : false;
    },
    [selectedLocationIds, customizationFlagIdEnumMap, customizationFlagMap, getCustomizationFlagValue]
  );

  const getScopedCustomizationFlagById = useCallback(
    (flagId: string) => {
      // TODO: Milind Remove customizationFlagMap fallback once all consumers use featureEnum
      const featureEnum = customizationFlagIdEnumMap[flagId];
      const flagName = customizationFlagMap[flagId];
      return getCustomizationFlag({ locationIds: selectedLocationIds, flagName, featureEnum });
    },
    [selectedLocationIds, customizationFlagIdEnumMap, customizationFlagMap, getCustomizationFlag]
  );

  const isFlagStoreLoaded = useMemo(
    () => selectedLocationIds.some((id) => featureFlags[id] !== undefined),
    [selectedLocationIds, featureFlags]
  );

  return {
    getFeatureFlagValue: getScopedFeatureFlagValue,
    getCustomizationFlagValue: getScopedCustomizationFlagValue,
    getCustomizationFlag: getScopedCustomizationFlag,
    getFeatureFlag: getScopedFeatureFlag,
    getCustomizationFlagLocationMap: getScopedCustomizationFlagLocationMap,
    getFeatureFlagLocationMap: getScopedFeatureFlagLocationMap,
    getCustomizationFlagValueById: getScopedCustomizationFlagValueById,
    getCustomizationFlagById: getScopedCustomizationFlagById,
    isFlagStoreLoaded,
  };
};
